// Manos de Oro™ — brand core (style guide v1, 6 Oct 2026). No dependencies: used by the Carta PDF (Deno), apps/mdo, mdo-ui.
// Sources: Ordenanzas de Albañilería (México, 1599), cartas de examen, marcas reticulares de cantero, marcas de platería
// novohispana (artífice · localidad · ensayador · quinto), escudos gremiales with the tools of the trade.

// ---------------------------------------------------------------- color: the pigments of New Spain
export const COLOR = {
  tinta:  "#2B231C", // tinta ferrogálica (iron-gall ink): all text and line work
  papel:  "#F4EEDF", // papel de algodón: document ground
  cal:    "#FBF8F1", // cal (lime wash): screen ground
  grana:  "#8F1D2C", // grana cochinilla (cochineal): seals, the mark, one accent per page
  oro:    "#B08A3E", // hoja de oro (gold leaf, retablos): rules and frames on the Carta only
  plata:  "#9EA3A8", // plata: secondary lines, disabled
  anil:   "#26406B", // añil (indigo): links and actions
} as const;

// One pigment per oficio (escudo field, progress bar, tile edge)
export const OFICIO = {
  plantacion:  { color: "#3E6B52", pigment: "verde cardenillo (verdigris)", es: "Plantación", en: "Planting" },
  poda:        { color: "#26406B", pigment: "añil (indigo)",                es: "Cuidado de Árboles y Arbustos", en: "Tree & Shrub Care" },
  hardscape:   { color: "#8A3A2C", pigment: "tezontle",                     es: "Hardscape",  en: "Hardscape" },
  concreto:    { color: "#7C746A", pigment: "cantera gris",                 es: "Concreto",   en: "Concrete" },
  cercado:     { color: "#9A5530", pigment: "almagre (red ochre)",          es: "Cercado",    en: "Fencing" },
  riego:       { color: "#1E56A0", pigment: "azul Talavera",                es: "Riego",      en: "Irrigation" },
  estructuras: { color: "#A07A2E", pigment: "oro viejo",                    es: "Estructuras", en: "Structures" },
} as const;
export type OficioKey = keyof typeof OFICIO;

// ---------------------------------------------------------------- type: Joaquín Ibarra's roman (Madrid, 18th c.)
export const TYPE = {
  serif: `"Ibarra Real Nova", "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif`,
  // App screens for workers keep the phone's own sans for legibility; Ibarra is for titles, Cartas and the site.
  ui: `system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`,
  googleFontsHref: "https://fonts.googleapis.com/css2?family=Ibarra+Real+Nova:ital,wght@0,400;0,600;1,400&display=swap",
} as const;

// ---------------------------------------------------------------- escudos: tools of the trade in a Spanish shield
// All paths live in a 0..100 box, stroke-only (engraved line), so they render the same in SVG and in pdf-lib.
export const ESCUDO_FRAME = "M6 4 H94 V56 Q94 96 50 96 Q6 96 6 56 Z";          // escudo español: square head, round point
export const ESCUDO_INNER = "M12 10 H88 V56 Q88 89 50 89 Q12 89 12 56 Z";

export const TOOL: Record<OficioKey, string> = {
  // Plantación — pala (spade) with a sapling
  plantacion: "M36 18 H50 M43 18 V58 M36 58 H50 L52 74 Q43 82 34 74 Z M64 74 V46 M64 56 Q56 50 56 42 Q64 44 64 56 M64 50 Q72 44 72 36 Q64 38 64 50",
  // Poda — tijeras de podar (pruning shears), crossed
  poda: "M34 22 Q46 34 52 48 M66 22 Q54 34 48 48 M52 48 L40 80 Q36 84 32 80 L46 50 M48 48 L60 80 Q64 84 68 80 L54 50 M50 46 m-3 0 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0",
  // Hardscape — mazo y cincel (mallet and chisel), the cantero's tools
  hardscape: "M28 24 H48 V40 H28 Z M38 40 L62 78 M56 26 L72 74 M52 22 H60 L58 30 H54 Z M70 72 L72 80 L74 72",
  // Concreto — llana (mason's flat finishing trowel): plate, two posts, bridge handle
  concreto: "M22 64 H78 Q82 64 82 69 Q82 74 78 74 H22 Q18 74 18 69 Q18 64 22 64 Z M40 64 V50 M60 64 V50 M34 50 H66 Q71 50 71 44 Q71 38 66 38 H34 Q29 38 29 44 Q29 50 34 50 Z",
  // Cercado — postes y travesaños (posts and rails)
  cercado: "M28 30 L32 24 L36 30 V80 H28 Z M46 30 L50 24 L54 30 V80 H46 Z M64 30 L68 24 L72 30 V80 H64 Z M24 42 H76 M24 64 H76",
  // Riego — gota (water drop) over a line with emitters
  riego: "M50 22 Q36 42 36 52 Q36 66 50 66 Q64 66 64 52 Q64 42 50 22 Z M24 78 H76 M34 78 V84 M50 78 V84 M66 78 V84",
  // Estructuras — escuadra y plomada (carpenter's square and plumb bob)
  estructuras: "M26 24 V76 H66 M34 24 V68 H66 M58 24 V48 M58 48 L52 60 L58 70 L64 60 Z",
};

/** A complete escudo as SVG. Field tinted with the oficio pigment; tool and frame in iron-gall ink. */
export function escudoSvg(oficio: OficioKey, size = 64, opts: { field?: boolean } = {}): string {
  const c = OFICIO[oficio].color, ink = COLOR.tinta;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}" role="img" aria-label="${OFICIO[oficio].es}">`
    + `<path d="${ESCUDO_FRAME}" fill="${opts.field === false ? "none" : c}" fill-opacity="0.14" stroke="${ink}" stroke-width="3"/>`
    + `<path d="${ESCUDO_INNER}" fill="none" stroke="${c}" stroke-width="1.5"/>`
    + `<path d="${TOOL[oficio]}" fill="none" stroke="${ink}" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

// ---------------------------------------------------------------- la marca: reticular cantero mark from one shared grid
// The plantilla: a 3×3 lattice (nodes at 0, 50, 100). Allowed cuts: the 12 orthogonal edges and the 8 cell diagonals.
// Each holder's mark is a connected figure of 4–6 cuts, chosen by a deterministic walk seeded from the Carta number:
// same Carta → same mark, forever; no one draws it by hand. Distinctive, not a unique ID (the Carta number is the ID).
type Node = [number, number];
const NODES: Node[] = [0, 50, 100].flatMap((y) => [0, 50, 100].map((x) => [x, y] as Node));
const idx = (x: number, y: number) => (y / 50) * 3 + x / 50;
export const GRID_SEGMENTS: [number, number][] = (() => {
  const s: [number, number][] = [];
  for (const [x, y] of NODES) {
    if (x < 100) s.push([idx(x, y), idx(x + 50, y)]);
    if (y < 100) s.push([idx(x, y), idx(x, y + 50)]);
    if (x < 100 && y < 100) { s.push([idx(x, y), idx(x + 50, y + 50)]); s.push([idx(x + 50, y), idx(x, y + 50)]); }
  }
  return s;
})();

function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
function mulberry32(a: number) {
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

/** The segments of a holder's mark (indices into GRID_SEGMENTS). Deterministic from the Carta number. */
export function markSegments(cartaNumber: string): number[] {
  const rnd = mulberry32(fnv1a(cartaNumber.trim().toUpperCase()));
  const want = 4 + Math.floor(rnd() * 3);                 // 4, 5 or 6 cuts
  const used = new Set<number>(), touched = new Set<number>([Math.floor(rnd() * 9)]);
  // always one vertical "stem" through the centre column or a side, like most cantero marks
  const stems = GRID_SEGMENTS.map((s, i) => [s, i] as const).filter(([[a, b]]) => NODES[a][0] === NODES[b][0]).map(([, i]) => i);
  const first = stems[Math.floor(rnd() * stems.length)];
  used.add(first); touched.clear(); touched.add(GRID_SEGMENTS[first][0]); touched.add(GRID_SEGMENTS[first][1]);
  while (used.size < want) {
    const options = GRID_SEGMENTS.map((s, i) => [s, i] as const)
      .filter(([[a, b], i]) => !used.has(i) && (touched.has(a) || touched.has(b))).map(([, i]) => i);
    const pick = options[Math.floor(rnd() * options.length)];
    used.add(pick); touched.add(GRID_SEGMENTS[pick][0]); touched.add(GRID_SEGMENTS[pick][1]);
  }
  return [...used].sort((a, b) => a - b);
}

/** The mark as an SVG path in a 0..100 box (inset so square caps stay inside). */
export function markPath(cartaNumber: string, inset = 12): string {
  const k = (100 - 2 * inset) / 100, p = (v: number) => +(inset + v * k).toFixed(2);
  return markSegments(cartaNumber).map((i) => {
    const [a, b] = GRID_SEGMENTS[i]; const [x1, y1] = NODES[a], [x2, y2] = NODES[b];
    return `M${p(x1)} ${p(y1)} L${p(x2)} ${p(y2)}`;
  }).join(" ");
}

/** The mark as a standalone SVG: incised lines in grana cochinilla (or ink), optional faint plantilla behind. */
export function markSvg(cartaNumber: string, size = 64, opts: { color?: string; grid?: boolean } = {}): string {
  const color = opts.color ?? COLOR.grana, inset = 12, k = (100 - 2 * inset) / 100, p = (v: number) => +(inset + v * k).toFixed(2);
  const grid = opts.grid ? GRID_SEGMENTS.map(([a, b]) => `M${p(NODES[a][0])} ${p(NODES[a][1])} L${p(NODES[b][0])} ${p(NODES[b][1])}`).join(" ") : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}" role="img" aria-label="Marca ${cartaNumber}">`
    + (grid ? `<path d="${grid}" stroke="${COLOR.plata}" stroke-width="1" fill="none" stroke-dasharray="2 3"/>` : "")
    + `<path d="${markPath(cartaNumber, inset)}" stroke="${color}" stroke-width="9" stroke-linecap="square" fill="none"/></svg>`;
}
